import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Info, HelpCircle, Calendar } from "lucide-react";
import { toast } from "sonner";
import { DisclaimerFooter } from "@/components/Disclaimer";
import { usePeriod } from "@/hooks/usePeriod";

// I 10 bisogni con definizioni ESPANSE (nuovo formato)
const NEEDS = [
  {
    id: 1,
    name: "Corpo / energia",
    shortName: "Corpo",
    description: "Risorse fisiche e mentali (sonno, cibo, pause, movimento, ritmo).",
    satisfiedExamples: "Dormo abbastanza / recupero; faccio pause vere; mangio con regolarità; ho energia 'ok'; mi muovo un po'.",
    unsatisfiedExamples: "Stanchezza costante; tensione/irritabilità da carico; salto pasti; giornate senza pause; vivo 'a spinta' (urgenze/caffeina)."
  },
  {
    id: 2,
    name: "Sicurezza / stabilità",
    shortName: "Sicurezza",
    description: "Basi affidabili (salute, casa, soldi, routine, prevedibilità, confini).",
    satisfiedExamples: "Ho un minimo di ordine (agenda/spese); ambiente stabile; mi sento protetto/a; confini chiari; gestisco gli imprevisti senza panico.",
    unsatisfiedExamples: "Preoccupazione continua; instabilità economica/organizzativa; sensazione di rischio; caos che mi disorienta; vivo 'in allerta'."
  },
  {
    id: 3,
    name: "Connessione / appartenenza",
    shortName: "Connessione",
    description: "Legami, supporto, sentirsi visti/ascoltati.",
    satisfiedExamples: "Parlo e mi sento capito/a; tempo di qualità; mi sento parte; chiedo aiuto; scambio affetto/cura.",
    unsatisfiedExamples: "Mi sento solo/a; mi chiudo; evito di dire cose importanti; mi sento escluso/a; relazioni fredde o conflittuali."
  },
  {
    id: 4,
    name: "Autonomia / scelta",
    shortName: "Autonomia",
    description: "Possibilità di scegliere, dire no, spazio personale, coerenza con valori.",
    satisfiedExamples: "Decido priorità; posso rinegoziare; dico no quando serve; faccio qualcosa 'mia'; mi rispetto.",
    unsatisfiedExamples: "Mi sento costretto/a; tutto imposto; non ho margine; compiacere per paura; mi sento 'senza voce'."
  },
  {
    id: 5,
    name: "Competenza / efficacia",
    shortName: "Competenza",
    description: "Sentirsi capace, fare progressi, gestire compiti e problemi.",
    satisfiedExamples: "Porto a termine; capisco il prossimo passo; gestisco bene un imprevisto; vedo un miglioramento; mi sento 'capace abbastanza'.",
    unsatisfiedExamples: "Blocco; procrastinazione; troppe cose; confusione su cosa fare; senso di inadeguatezza."
  },
  {
    id: 6,
    name: "Stima / valore personale",
    shortName: "Stima",
    description: "Rispetto (anche da sé), dignità, riconoscimento.",
    satisfiedExamples: "Mi tratto con rispetto; riconosco sforzi; ricevo considerazione; metto limiti quando vengo sminuito/a; mi sento 'a posto con me'.",
    unsatisfiedExamples: "Autosvalutazione; confronto tossico; critiche che mi abbattono; accetto mancanza di rispetto; vergogna o colpa eccessive."
  },
  {
    id: 7,
    name: "Comprensione / chiarezza",
    shortName: "Comprensione",
    description: "Orientamento mentale: capire cosa succede, dare un senso, sapere cosa fare.",
    satisfiedExamples: "Capisco cosa provo; ho informazioni sufficienti; faccio ordine; vedo priorità; scelgo una direzione.",
    unsatisfiedExamples: "Confusione; ruminazione; troppe opzioni; non capisco 'perché'; mi sento perso/a."
  },
  {
    id: 8,
    name: "Partecipazione / contributo",
    shortName: "Partecipazione",
    description: "Sentirsi parte attiva, avere voce, contribuire, incidere.",
    satisfiedExamples: "Partecipo a decisioni; il mio contributo conta; mi sento utile; collaborazione buona; scambio equo.",
    unsatisfiedExamples: "Esclusione; 'decidono gli altri'; invisibilità; contributo ignorato; sensazione di inutilità."
  },
  {
    id: 9,
    name: "Identità / senso",
    shortName: "Identità",
    description: "Coerenza con sé, significato, direzione, espressione autentica.",
    satisfiedExamples: "Faccio scelte che mi rappresentano; sento un perché; coltivo ciò che per me conta; mi riconosco; ho un tema guida.",
    unsatisfiedExamples: "Vuoto o apatia; vado in automatico; faccio cose che non mi somigliano; disconnessione da me; perdita di significato."
  },
  {
    id: 10,
    name: "Svago / gioco / creatività",
    shortName: "Svago",
    description: "Piacere, leggerezza, curiosità, decompressione, creatività.",
    satisfiedExamples: "Ho un momento di piacere gratuito; hobby/curiosità; rido; creo qualcosa; stacco mentalmente.",
    unsatisfiedExamples: "Tutto dovere; niente stacco; rigidità; 'non ho tempo per me'; arrivo a fine giornata saturo/a ma non bene."
  }
];

const STATUS_COLORS = {
  satisfied: { bg: "#22c55e", label: "Prevalentemente soddisfatto", labelShort: "Soddisfatto" },
  unsatisfied: { bg: "#ef4444", label: "Prevalentemente non soddisfatto", labelShort: "Insoddisfatto" },
  neutral: { bg: "#e5e5e5", label: "Non rilevante oggi", labelShort: "" }
};

// Consigli pratici per ogni bisogno (30 per bisogno) - indicizzati per anti-ripetizione
const NEEDS_TIPS = {
  1: [ // Corpo / energia
    "Fai 5 respiri profondi, espirando lentamente",
    "Bevi un bicchiere d'acqua ora",
    "Alzati e fai 2 minuti di stretching",
    "Esci per una passeggiata di 10 minuti",
    "Fai una pausa caffè senza telefono",
    "Metti una sveglia per andare a letto entro le 23",
    "Prova 5 minuti di meditazione guidata",
    "Fai 10 squat o jumping jack",
    "Massaggiati le spalle per 1 minuto",
    "Prepara uno spuntino sano",
    "Stenditi per 5 minuti con le gambe in alto",
    "Fai una doccia calda rilassante",
    "Evita il caffè dopo le 15",
    "Prova a mangiare più lentamente al prossimo pasto",
    "Fai 3 piegamenti o plank per 30 secondi",
    "Ascolta il tuo corpo: di cosa ha bisogno ora?",
    "Prova un bagno caldo con sali",
    "Spegni gli schermi 30 minuti prima di dormire",
    "Fai una camminata dopo pranzo",
    "Prova lo yoga per 10 minuti",
    "Siediti al sole per 5 minuti",
    "Fai esercizi per il collo e le spalle",
    "Prepara la cena in anticipo per stasera",
    "Prova un power nap di 20 minuti",
    "Balla su una canzone che ti piace",
    "Fai giardinaggio o cura le piante",
    "Prova il dry brushing prima della doccia",
    "Cammina scalzo sull'erba",
    "Fai 50 passi ogni ora di lavoro sedentario",
    "Prova una tisana rilassante prima di dormire"
  ],
  2: [ // Sicurezza / stabilità
    "Scrivi 3 cose che puoi controllare oggi",
    "Prepara i vestiti per domani stasera",
    "Controlla il tuo budget della settimana",
    "Crea una routine mattutina di 15 minuti",
    "Fai un backup dei tuoi file importanti",
    "Organizza un cassetto o un angolo di casa",
    "Prepara un piccolo fondo di emergenza",
    "Fissa un appuntamento medico rimandato",
    "Scrivi una lista di cose da fare per domani",
    "Pulisci e ordina la scrivania",
    "Controlla le scadenze importanti del mese",
    "Crea una routine serale rilassante",
    "Fai un controllo delle spese fisse",
    "Pianifica i pasti della settimana",
    "Metti in ordine i documenti importanti",
    "Crea un piano B per una preoccupazione attuale",
    "Verifica che le porte siano chiuse prima di dormire",
    "Organizza gli appuntamenti della settimana",
    "Fai una lista della spesa prima di uscire",
    "Sistema l'armadio per stagione",
    "Controlla la manutenzione dell'auto o della casa",
    "Imposta pagamenti automatici per le bollette",
    "Crea un rituale di chiusura della giornata lavorativa",
    "Prepara la borsa/zaino la sera prima",
    "Fai un inventario delle scorte in dispensa",
    "Pianifica un momento di revisione settimanale",
    "Assicurati di avere contatti di emergenza salvati",
    "Crea un angolo di calma in casa",
    "Stabilisci orari fissi per i pasti",
    "Programma del tempo libero nel calendario"
  ],
  3: [ // Connessione / appartenenza
    "Manda un messaggio a un amico che non senti da tempo",
    "Chiama un familiare per 5 minuti",
    "Scrivi un biglietto di ringraziamento a qualcuno",
    "Proponi un caffè a un collega",
    "Abbraccia qualcuno per almeno 20 secondi",
    "Fai un complimento sincero a qualcuno oggi",
    "Ascolta attivamente qualcuno senza interrompere",
    "Condividi come ti senti con una persona fidata",
    "Organizza una cena con amici",
    "Unisciti a un gruppo o un'associazione",
    "Fai volontariato per una causa che ti sta a cuore",
    "Scrivi una lettera (vera) a qualcuno",
    "Chiedi aiuto per qualcosa",
    "Racconta un ricordo felice a qualcuno",
    "Fai una videochiamata invece di un messaggio",
    "Proponi una passeggiata insieme a qualcuno",
    "Condividi un pasto con qualcuno",
    "Gioca a un gioco da tavolo in compagnia",
    "Partecipa a un evento della comunità",
    "Fai un regalo inaspettato a qualcuno",
    "Chiedi a qualcuno come sta veramente",
    "Organizza una serata film con amici o famiglia",
    "Iscriviti a un corso di gruppo",
    "Fai un gesto gentile per un vicino",
    "Condividi una tua vulnerabilità con qualcuno",
    "Crea un rituale settimanale con persone care",
    "Rispondi con più attenzione ai messaggi",
    "Fai una foto insieme a qualcuno e condividila",
    "Ascolta un podcast insieme a qualcuno",
    "Crea un gruppo WhatsApp per un interesse comune"
  ],
  4: [ // Autonomia / scelta
    "Dì di no a qualcosa che non vuoi fare",
    "Scegli tu cosa mangiare oggi",
    "Prenditi 30 minuti solo per te",
    "Decidi tu l'orario di una cosa",
    "Esprimi una preferenza invece di dire 'come vuoi tu'",
    "Fai qualcosa solo perché ti va",
    "Stabilisci un confine con qualcuno",
    "Spegni le notifiche per un'ora",
    "Scegli di non rispondere subito a un messaggio",
    "Prenditi una pausa quando ne hai bisogno",
    "Decidi tu come trascorrere la serata",
    "Rifiuta un invito se non ti va",
    "Fai una scelta senza chiedere approvazione",
    "Cambia un'abitudine che non ti rappresenta più",
    "Dedica tempo a un tuo interesse personale",
    "Scegli di fare le cose al tuo ritmo",
    "Esprimi il tuo disaccordo in modo rispettoso",
    "Prenditi spazio fisico quando ne hai bisogno",
    "Decidi tu la musica da ascoltare",
    "Fai una passeggiata da solo/a",
    "Scegli di delegare qualcosa",
    "Personalizza il tuo spazio di lavoro",
    "Decidi di eliminare un impegno superfluo",
    "Fai qualcosa di diverso dalla routine",
    "Scegli tu il film o la serie da vedere",
    "Prenditi libertà nella tua creatività",
    "Decidi un nuovo obiettivo personale",
    "Rinegozia un accordo che non ti soddisfa",
    "Fai qualcosa di spontaneo",
    "Concediti il permesso di cambiare idea"
  ],
  5: [ // Competenza / efficacia
    "Completa una piccola task che rimandi",
    "Dividi un progetto grande in passi più piccoli",
    "Festeggia un piccolo successo di oggi",
    "Impara una cosa nuova in 10 minuti",
    "Chiedi feedback su qualcosa che hai fatto",
    "Rivedi e aggiorna la tua to-do list",
    "Finisci qualcosa al 80% invece che perfetto",
    "Insegna qualcosa a qualcuno",
    "Risolvi un problema che rimandi",
    "Fai una cosa che ti spaventa un po'",
    "Riorganizza le priorità della settimana",
    "Completa un corso online o un tutorial",
    "Scrivi cosa hai imparato oggi",
    "Chiedi consiglio a un esperto",
    "Fai un passo verso un obiettivo a lungo termine",
    "Semplifica un processo che usi spesso",
    "Elimina una distrazione dal tuo ambiente",
    "Pratica una skill per 15 minuti",
    "Documenta un tuo successo passato",
    "Prepara meglio per una sfida futura",
    "Automatizza qualcosa di ripetitivo",
    "Fai una revisione di cosa funziona e cosa no",
    "Chiudi un ciclo aperto (email, task, promessa)",
    "Migliora qualcosa di piccolo nel tuo lavoro",
    "Sperimenta un nuovo metodo o strumento",
    "Concediti di sbagliare per imparare",
    "Rifletti su un feedback ricevuto",
    "Identifica il prossimo passo più utile",
    "Delega qualcosa che non devi fare tu",
    "Riconosci quanto sei migliorato in qualcosa"
  ],
  6: [ // Stima / valore personale
    "Scrivi 3 cose che apprezzi di te",
    "Accetta un complimento senza sminuirlo",
    "Riconosci uno sforzo che hai fatto oggi",
    "Trattati come tratteresti un amico caro",
    "Smetti di confrontarti con gli altri per oggi",
    "Fai qualcosa che ti fa sentire bene con te stesso/a",
    "Difendi un tuo confine",
    "Perdona te stesso/a per un errore",
    "Vestiti in un modo che ti fa sentire bene",
    "Riconosci i tuoi progressi, non solo i risultati",
    "Di' a te stesso/a qualcosa di gentile",
    "Rifiuta di accettare critiche non costruttive",
    "Celebra qualcosa che hai fatto bene",
    "Prenditi cura del tuo aspetto",
    "Ricorda un momento in cui sei stato/a orgoglioso/a",
    "Smetti di scusarti per cose che non richiedono scuse",
    "Fai un'affermazione positiva su di te",
    "Riconosci le tue qualità uniche",
    "Non minimizzare i tuoi successi",
    "Concediti qualcosa di piacevole",
    "Esponi la tua opinione anche se diversa",
    "Riconosci quanto sei resiliente",
    "Tratta il tuo tempo come prezioso",
    "Rifiuta di fare qualcosa che ti svaluta",
    "Circondati di persone che ti valorizzano",
    "Scrivi una lista di tuoi traguardi",
    "Ricorda che meriti rispetto",
    "Fai qualcosa che alimenta la tua autostima",
    "Smetti di cercare approvazione esterna oggi",
    "Riconosci il tuo valore indipendentemente dai risultati"
  ],
  7: [ // Comprensione / chiarezza
    "Scrivi cosa ti preoccupa per fare chiarezza",
    "Fai una domanda a qualcuno che può aiutarti",
    "Cerca informazioni su qualcosa che non capisci",
    "Fai una lista pro/contro per una decisione",
    "Identifica cosa puoi controllare e cosa no",
    "Parla ad alta voce di un problema per capirlo",
    "Semplifica una situazione complessa",
    "Chiedi chiarimenti invece di assumere",
    "Fai un brainstorm di possibili soluzioni",
    "Scrivi i tuoi pensieri per 5 minuti",
    "Identifica la vera domanda dietro la confusione",
    "Cerca un punto di vista diverso dal tuo",
    "Fai ordine fisico per fare ordine mentale",
    "Dai un nome a quello che senti",
    "Riduci le opzioni a massimo 3",
    "Chiedi feedback a qualcuno di fiducia",
    "Dormi su una decisione importante",
    "Visualizza gli scenari possibili",
    "Identifica la cosa più importante da capire ora",
    "Lascia andare la necessità di capire tutto",
    "Fai una pausa dalla ruminazione",
    "Scrivi cosa sai vs cosa non sai",
    "Cerca pattern ricorrenti nella situazione",
    "Accetta l'incertezza come parte del processo",
    "Rivedi informazioni che già hai",
    "Fai una mappa mentale del problema",
    "Concentrati su un solo aspetto alla volta",
    "Chiedi 'qual è il prossimo passo più piccolo?'",
    "Ricorda che la chiarezza arriva anche facendo",
    "Distingui tra urgente e importante"
  ],
  8: [ // Partecipazione / contributo
    "Offri aiuto a qualcuno oggi",
    "Condividi un'idea in una riunione o gruppo",
    "Partecipa attivamente a una discussione",
    "Fai volontariato anche solo per un'ora",
    "Dai un feedback costruttivo a qualcuno",
    "Proponi un'iniziativa o un miglioramento",
    "Chiedi di essere coinvolto/a in qualcosa",
    "Condividi una tua competenza con altri",
    "Contribuisci a un progetto di squadra",
    "Fai una donazione a una causa",
    "Partecipa a una decisione familiare",
    "Offri il tuo punto di vista quando richiesto",
    "Aiuta un collega con un problema",
    "Partecipa a un evento della comunità",
    "Scrivi una recensione utile per altri",
    "Fai da mentore a qualcuno meno esperto",
    "Condividi risorse utili con il tuo gruppo",
    "Proponi un'attività di gruppo",
    "Riconosci il contributo degli altri",
    "Chiedi come puoi essere più utile",
    "Partecipa a una causa sociale",
    "Condividi le tue esperienze per aiutare altri",
    "Collabora invece di competere",
    "Fai sentire la tua voce su qualcosa che conta",
    "Crea qualcosa che altri possono usare",
    "Aiuta a risolvere un conflitto",
    "Partecipa a una decisione di lavoro",
    "Sostieni pubblicamente qualcuno",
    "Fai parte di un progetto collettivo",
    "Riconosci quando il tuo contributo ha fatto la differenza"
  ],
  9: [ // Identità / senso
    "Fai qualcosa in linea con i tuoi valori",
    "Rifletti su cosa ti rende unico/a",
    "Dedica tempo a una passione personale",
    "Scrivi cosa è importante per te",
    "Fai una scelta autentica, anche piccola",
    "Ricorda perché fai quello che fai",
    "Esprimi la tua personalità in qualcosa",
    "Riconnettiti con un vecchio interesse",
    "Rifletti su un momento significativo",
    "Fai qualcosa che ti rappresenta",
    "Scrivi i tuoi valori più importanti",
    "Chiediti se le tue azioni riflettono chi sei",
    "Fai spazio per la tua spiritualità o riflessione",
    "Rifiuta qualcosa che non ti rappresenta",
    "Coltiva una relazione significativa",
    "Rifletti sul tuo percorso e progressi",
    "Fai qualcosa di creativo che ti esprima",
    "Connettiti con la tua storia personale",
    "Identifica cosa dà senso alla tua giornata",
    "Fai un gesto che riflette i tuoi ideali",
    "Ricorda le tue radici e da dove vieni",
    "Definisci un obiettivo allineato con te",
    "Circondati di cose che ti rappresentano",
    "Lascia andare qualcosa che non ti somiglia più",
    "Celebra la tua individualità",
    "Scrivi una lettera al te stesso/a del futuro",
    "Rifletti su cosa vuoi lasciare come eredità",
    "Fai qualcosa solo perché ha significato per te",
    "Connettiti con persone che condividono i tuoi valori",
    "Ricorda chi vuoi essere, non solo cosa vuoi fare"
  ],
  10: [ // Svago / gioco / creatività
    "Guarda qualcosa che ti fa ridere",
    "Gioca a un gioco (da tavolo, videogioco, carte)",
    "Ascolta musica che ti piace",
    "Fai qualcosa di creativo senza obiettivo",
    "Leggi qualcosa per puro piacere",
    "Guarda un film o una serie che ti attira",
    "Fai una cosa nuova per curiosità",
    "Disegna, colora o scrivi liberamente",
    "Balla senza pensare",
    "Esplora un posto nuovo",
    "Gioca con un animale o un bambino",
    "Fai qualcosa di sciocco intenzionalmente",
    "Cucina qualcosa di nuovo per piacere",
    "Fai giardinaggio o un lavoretto creativo",
    "Vai a un evento culturale (mostra, concerto)",
    "Improvvisa qualcosa senza pianificare",
    "Fai un puzzle o un gioco di logica",
    "Guarda video divertenti online",
    "Suona uno strumento o canta",
    "Fai sport per divertimento, non per dovere",
    "Organizza una serata giochi con amici",
    "Scopri un nuovo hobby",
    "Fai una gita fuori porta spontanea",
    "Permetti alla tua mente di vagare",
    "Ridi di te stesso/a",
    "Fai qualcosa che facevi da bambino/a",
    "Crea qualcosa con le mani",
    "Guarda le stelle o il tramonto",
    "Concediti un piacere senza sensi di colpa",
    "Fai qualcosa solo perché ti va, senza motivo"
  ]
};

// Componente Ruota
const NeedsWheel = ({ needsStatus, onNeedClick, selectedNeed }) => {
  const [size, setSize] = useState(300);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 400) setSize(260);
      else if (width < 640) setSize(300);
      else setSize(340);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const center = size / 2;
  const outerRadius = size * 0.44;
  const innerRadius = size * 0.16;
  const segments = 10;
  const anglePerSegment = (2 * Math.PI) / segments;

  const getSegmentPath = (index) => {
    const startAngle = index * anglePerSegment - Math.PI / 2;
    const endAngle = startAngle + anglePerSegment;
    
    const x1Outer = center + outerRadius * Math.cos(startAngle);
    const y1Outer = center + outerRadius * Math.sin(startAngle);
    const x2Outer = center + outerRadius * Math.cos(endAngle);
    const y2Outer = center + outerRadius * Math.sin(endAngle);
    
    const x1Inner = center + innerRadius * Math.cos(startAngle);
    const y1Inner = center + innerRadius * Math.sin(startAngle);
    const x2Inner = center + innerRadius * Math.cos(endAngle);
    const y2Inner = center + innerRadius * Math.sin(endAngle);

    return `
      M ${x1Inner} ${y1Inner}
      L ${x1Outer} ${y1Outer}
      A ${outerRadius} ${outerRadius} 0 0 1 ${x2Outer} ${y2Outer}
      L ${x2Inner} ${y2Inner}
      A ${innerRadius} ${innerRadius} 0 0 0 ${x1Inner} ${y1Inner}
      Z
    `;
  };

  const getLabelPosition = (index) => {
    const angle = index * anglePerSegment - Math.PI / 2 + anglePerSegment / 2;
    const labelRadius = (outerRadius + innerRadius) / 2;
    return {
      x: center + labelRadius * Math.cos(angle),
      y: center + labelRadius * Math.sin(angle)
    };
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      {NEEDS.map((need, index) => {
        const status = needsStatus[need.id] || "neutral";
        const isSelected = selectedNeed === need.id;
        const pos = getLabelPosition(index);
        
        return (
          <g key={need.id}>
            <path
              d={getSegmentPath(index)}
              fill={STATUS_COLORS[status].bg}
              stroke={isSelected ? "#1C1917" : "#E7E5E4"}
              strokeWidth={isSelected ? 3 : 1.5}
              className="cursor-pointer transition-all duration-200 hover:opacity-80"
              onClick={() => onNeedClick(need.id)}
              data-testid={`wheel-segment-${need.id}`}
            />
            <text
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-[10px] font-medium pointer-events-none select-none"
              fill={status === "neutral" ? "#78716C" : "#ffffff"}
            >
              {need.id}
            </text>
          </g>
        );
      })}
      
      <circle
        cx={center}
        cy={center}
        r={innerRadius - 5}
        fill="#FAFAF9"
        stroke="#E7E5E4"
        strokeWidth={1}
      />
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="middle"
        className="text-sm font-heading font-medium"
        fill="#1C1917"
      >
        OGGI
      </text>
    </svg>
  );
};

const NeedsPage = () => {
  const { periodStartDate } = usePeriod();
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [currentEntry, setCurrentEntry] = useState({
    needs_status: {},
    influences: "",
    main_needs: []
  });
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [shownTips, setShownTips] = useState({}); // Track shown tips from backend

  // Fetch shown tips on mount
  useEffect(() => {
    const fetchShownTips = async () => {
      try {
        const response = await api.get('/needs/tips/shown');
        setShownTips(response.data.shown_tips || {});
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // Non-critical: tips tracking unavailable
        }
      }
    };
    fetchShownTips();
  }, []);

  useEffect(() => {
    fetchNeedsEntry();
  }, [selectedDate]);

  const fetchNeedsEntry = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/needs/${selectedDate}`);
      if (response.data && response.data.needs_status) {
        setCurrentEntry({
          needs_status: response.data.needs_status,
          influences: response.data.influences || "",
          main_needs: response.data.main_needs || []
        });
      } else {
        setCurrentEntry({
          needs_status: {},
          influences: "",
          main_needs: []
        });
      }
    } catch (error) {
      setCurrentEntry({
        needs_status: {},
        influences: "",
        main_needs: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNeedClick = (needId) => {
    setSelectedNeed(needId);
    
    const currentStatus = currentEntry.needs_status[needId] || "neutral";
    let newStatus;
    if (currentStatus === "neutral") newStatus = "satisfied";
    else if (currentStatus === "satisfied") newStatus = "unsatisfied";
    else newStatus = "neutral";

    setCurrentEntry(prev => ({
      ...prev,
      needs_status: {
        ...prev.needs_status,
        [needId]: newStatus
      }
    }));
  };

  const handleSave = async () => {
    try {
      await api.post(`/needs`, {
        date: selectedDate,
        needs_status: currentEntry.needs_status,
        influences: currentEntry.influences || "",
        micro_action: "", // Rimosso ma manteniamo per compatibilità DB
        main_needs: currentEntry.main_needs || []
      });
      toast.success("Ruota dei bisogni salvata!");
    } catch (error) {
      toast.error("Errore nel salvataggio: " + (error.response?.data?.detail || error.message));
    }
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const formatDate = () => {
    return new Date(selectedDate).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const getSelectedNeedInfo = () => {
    if (!selectedNeed) return null;
    return NEEDS.find(n => n.id === selectedNeed);
  };

  // Memoize status counts
  const statusCounts = useMemo(() => ({
    satisfied: Object.values(currentEntry.needs_status).filter(s => s === "satisfied").length,
    unsatisfied: Object.values(currentEntry.needs_status).filter(s => s === "unsatisfied").length,
    neutral: Object.values(currentEntry.needs_status).filter(s => s === "neutral").length
  }), [currentEntry.needs_status]);

  const countByStatus = (status) => statusCounts[status] || 0;

  // Get tips for a need, avoiding recently shown ones
  const getTipsForNeed = useCallback((needId) => {
    const tips = NEEDS_TIPS[needId] || [];
    if (tips.length === 0) return { tips: [], indices: [] };
    
    // Get indices of recently shown tips
    const recentlyShown = (shownTips[needId] || []).map(t => t.index);
    
    // Get available tips (not recently shown)
    let available = tips.map((_, idx) => idx).filter(idx => !recentlyShown.includes(idx));
    
    // If not enough available, use all tips but prefer not recently shown
    if (available.length < 3) {
      available = tips.map((_, idx) => idx);
    }
    
    // Seed based on date for deterministic but varying selection
    const dateObj = new Date(selectedDate);
    const dateSeed = dateObj.getFullYear() * 10000 + 
                    (dateObj.getMonth() + 1) * 100 + 
                    dateObj.getDate() + 
                    needId * 31;
    
    const seededRandom = (seed, index) => {
      const x = Math.sin(seed + index) * 10000;
      return x - Math.floor(x);
    };
    
    // Shuffle available indices
    const shuffled = [...available];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom(dateSeed, i) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    const selectedIndices = shuffled.slice(0, 3);
    return {
      tips: selectedIndices.map(idx => tips[idx]),
      indices: selectedIndices
    };
  }, [selectedDate, shownTips]);

  // Track shown tips when displaying advice
  const trackShownTips = useCallback(async (needId, indices) => {
    try {
      await api.post('/needs/tips/shown', {
        need_id: needId,
        tip_indices: indices
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // Non-critical: tips tracking failed
      }
    }
  }, []);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="needs-page">
      {/* Period Banner */}
      {periodStartDate && (
        <div className="bg-muted/50 border border-border rounded-sm p-3 flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            Visualizzando dati dal <span className="font-medium text-foreground">{new Date(periodStartDate).toLocaleDateString('it-IT')}</span>
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h1 className="page-title">Bisogni</h1>
          <p className="page-subtitle">Ruota giornaliera dei 10 bisogni</p>
        </div>
        <Dialog open={showGuide} onOpenChange={setShowGuide}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="show-guide-btn" className="rounded-sm w-full sm:w-auto">
              <HelpCircle className="w-4 h-4 mr-2" /> Guida
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Come compilare la Ruota dei Bisogni</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 pt-4">
              <div>
                <ol className="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
                  <li><strong>Scegli cosa tracciare:</strong> non serve colorare tutti i bisogni; segna solo quelli che oggi sono stati importanti.</li>
                  <li><strong>Clicca sul numero (1–10):</strong> aprirai la scheda del bisogno con descrizione ed esempi.</li>
                  <li><strong>Leggi gli esempi:</strong> aiutano a riconoscere il bisogno nella tua giornata.</li>
                  <li><strong>Scegli lo stato:</strong>
                    <ul className="ml-6 mt-2 space-y-1">
                      <li><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span><strong>Verde</strong> = prevalentemente soddisfatto</li>
                      <li><span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-2"></span><strong>Rosso</strong> = prevalentemente non soddisfatto</li>
                      <li><span className="inline-block w-3 h-3 rounded-full border border-border mr-2"></span><strong>Vuoto</strong> = oggi non lo traccio / non è stato rilevante</li>
                    </ul>
                  </li>
                  <li><strong>Se sei indeciso/a:</strong> scegli in base a "com'è andata nel complesso", non al dettaglio.</li>
                  <li><strong>Extra (facoltativo):</strong> scrivi 1 riga su cosa ha influito.</li>
                </ol>
              </div>
              
              <div className="bg-muted/50 p-3 rounded-sm text-sm">
                <p className="font-medium mb-1">Nota per i pulsanti rapidi nell'app:</p>
                <p className="text-muted-foreground">
                  Quando compili in modalità veloce, puoi usare i tasti <strong>Soddisfatto / Insoddisfatto</strong>. 
                  Se oggi è stato "a metà", scegli l'opzione che descrive meglio la giornata <strong>nel complesso</strong> oppure lascia vuoto.
                </p>
              </div>
              
              <div className="border-t border-border pt-4">
                <h3 className="font-medium mb-3">I 10 Bisogni</h3>
                <div className="space-y-3">
                  {NEEDS.map(need => (
                    <div key={need.id} className="p-3 bg-muted rounded-sm">
                      <p className="font-medium text-sm">{need.id}. {need.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{need.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-muted-foreground italic border-t border-border pt-4">
                Questo strumento è pensato per l'auto-riflessione e l'organizzazione personale; 
                non sostituisce un parere medico o psicologico.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)} data-testid="prev-day-btn">
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Giorno prec.</span>
        </Button>
        <span className="font-heading text-sm sm:text-lg capitalize">{formatDate()}</span>
        <Button variant="ghost" size="sm" onClick={() => navigateDate(1)} data-testid="next-day-btn">
          <span className="hidden sm:inline mr-1">Giorno succ.</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-green-500"></div>
          <span>Soddisfatto</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-red-500"></div>
          <span>Insoddisfatto</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-border"></div>
          <span>Non rilevante</span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Wheel */}
          <Card className="lg:col-span-2 border border-border shadow-sm rounded-sm" data-testid="needs-wheel-card">
            <CardContent className="p-6">
              <NeedsWheel 
                needsStatus={currentEntry.needs_status}
                onNeedClick={handleNeedClick}
                selectedNeed={selectedNeed}
              />
              
              {/* Stats */}
              <div className="flex justify-center gap-8 mt-6 text-sm">
                <div className="text-center">
                  <p className="text-2xl font-mono font-medium text-green-600">{countByStatus("satisfied")}</p>
                  <p className="text-xs text-muted-foreground">Soddisfatti</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-mono font-medium text-red-600">{countByStatus("unsatisfied")}</p>
                  <p className="text-xs text-muted-foreground">Insoddisfatti</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-mono font-medium text-muted-foreground">
                    {10 - countByStatus("satisfied") - countByStatus("unsatisfied")}
                  </p>
                  <p className="text-xs text-muted-foreground">Non tracciati</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Need Details & Codes */}
          <div className="space-y-6">
            {/* Selected Need Info */}
            {getSelectedNeedInfo() ? (
              <Card className="border border-border shadow-sm rounded-sm" data-testid="selected-need-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-foreground text-background text-xs flex items-center justify-center">
                      {selectedNeed}
                    </span>
                    {getSelectedNeedInfo().name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">{getSelectedNeedInfo().description}</p>
                  
                  <div className="space-y-2 pt-2">
                    <div className="p-2 bg-green-50 rounded-sm border border-green-200">
                      <p className="text-xs font-medium text-green-700">✓ Prevalentemente soddisfatto</p>
                      <p className="text-xs text-green-600 mt-1">{getSelectedNeedInfo().satisfiedExamples}</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded-sm border border-red-200">
                      <p className="text-xs font-medium text-red-700">✗ Prevalentemente non soddisfatto</p>
                      <p className="text-xs text-red-600 mt-1">{getSelectedNeedInfo().unsatisfiedExamples}</p>
                    </div>
                  </div>

                  {/* Quick status buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant={currentEntry.needs_status[selectedNeed] === "satisfied" ? "default" : "outline"}
                      className="flex-1 rounded-sm"
                      onClick={() => {
                        setCurrentEntry(prev => ({
                          ...prev,
                          needs_status: { ...prev.needs_status, [selectedNeed]: "satisfied" }
                        }));
                      }}
                      data-testid="set-satisfied-btn"
                    >
                      ✓ Soddisfatto
                    </Button>
                    <Button
                      size="sm"
                      variant={currentEntry.needs_status[selectedNeed] === "unsatisfied" ? "destructive" : "outline"}
                      className="flex-1 rounded-sm"
                      onClick={() => {
                        setCurrentEntry(prev => ({
                          ...prev,
                          needs_status: { ...prev.needs_status, [selectedNeed]: "unsatisfied" }
                        }));
                      }}
                      data-testid="set-unsatisfied-btn"
                    >
                      ✗ Insoddisfatto
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border border-border shadow-sm rounded-sm">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Clicca su uno spicchio della ruota per vedere i dettagli del bisogno</p>
                </CardContent>
              </Card>
            )}

            {/* Codes Reference */}
            <Card className="border border-border shadow-sm rounded-sm" data-testid="codes-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm">Codici (1-10)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  {NEEDS.map(need => {
                    const status = currentEntry.needs_status[need.id];
                    return (
                      <button
                        key={need.id}
                        onClick={() => setSelectedNeed(need.id)}
                        className={`text-left p-1.5 rounded-sm transition-colors hover:bg-muted ${
                          selectedNeed === need.id ? "bg-muted" : ""
                        }`}
                      >
                        <span className={`inline-block w-4 h-4 rounded-full text-[10px] leading-4 text-center mr-1 ${
                          status === "satisfied" ? "bg-green-500 text-white" :
                          status === "unsatisfied" ? "bg-red-500 text-white" :
                          "border border-border"
                        }`}>
                          {need.id}
                        </span>
                        <span className="text-muted-foreground">{need.shortName}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Notes Section - Solo "Cosa ha influito" */}
      <Card className="border border-border shadow-sm rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Cosa ha influito</CardTitle>
          <p className="text-xs text-muted-foreground">1-2 righe su cosa ha impattato i tuoi bisogni oggi</p>
        </CardHeader>
        <CardContent>
          <Textarea
            data-testid="influences-input"
            value={currentEntry.influences || ""}
            onChange={(e) => setCurrentEntry(prev => ({ ...prev, influences: e.target.value }))}
            placeholder="Es: La riunione ha tolto energia, ma il feedback positivo mi ha fatto sentire valorizzato..."
            className="rounded-sm min-h-[80px]"
          />
        </CardContent>
      </Card>

      <Button onClick={handleSave} data-testid="save-needs-btn" className="rounded-sm">
        Salva Ruota dei Bisogni
      </Button>

      {/* Consigli Pratici per Bisogni Insoddisfatti - con anti-ripetizione */}
      {(() => {
        const unsatisfiedNeeds = NEEDS.filter(n => currentEntry.needs_status[n.id] === 'unsatisfied');
        
        if (unsatisfiedNeeds.length === 0) return null;

        return (
          <Card className="border-2 border-red-200 bg-red-50/30 shadow-sm rounded-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <span className="text-red-500">💡</span>
                Consigli per i Bisogni Insoddisfatti
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Piccole azioni che puoi fare oggi per prenderti cura di te (cambiano ogni giorno)
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {unsatisfiedNeeds.map(need => {
                const { tips, indices } = getTipsForNeed(need.id);
                // Track shown tips (fire and forget)
                if (indices.length > 0) {
                  trackShownTips(need.id, indices);
                }
                return (
                  <div key={need.id} className="space-y-2">
                    <h4 className="font-medium text-sm flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                        {need.id}
                      </span>
                      {need.shortName}
                    </h4>
                    <ul className="space-y-1 pl-7">
                      {tips.map((tip, idx) => (
                        <li key={`tip-${need.id}-${idx}`} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-red-400 mt-1">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })()}

      {/* Riepilogo di Oggi */}
      {Object.keys(currentEntry.needs_status).length > 0 && (
        <Card className="border border-border shadow-sm rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Riepilogo di Oggi</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-4 bg-green-50 rounded-sm border border-green-200">
                <p className="text-3xl font-bold text-green-600">{countByStatus('satisfied')}</p>
                <p className="text-sm text-green-700">Soddisfatti</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-sm border border-red-200">
                <p className="text-3xl font-bold text-red-600">{countByStatus('unsatisfied')}</p>
                <p className="text-sm text-red-700">Insoddisfatti</p>
              </div>
            </div>
            
            <div className="space-y-2">
              {NEEDS.map(need => {
                const status = currentEntry.needs_status[need.id];
                return (
                  <div key={need.id} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full ${
                      status === 'satisfied' ? 'bg-green-500' : 
                      status === 'unsatisfied' ? 'bg-red-500' : 
                      'bg-gray-200'
                    }`}></div>
                    <span className={`text-sm ${
                      status === 'satisfied' ? 'text-green-700' : 
                      status === 'unsatisfied' ? 'text-red-700' : 
                      'text-gray-400'
                    }`}>
                      {need.shortName}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <DisclaimerFooter />
    </div>
  );
};

export default NeedsPage;
